/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Cell,
  DateRange,
  Grid,
  Heading,
  HFlow,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  Theme,
  Tooltip,
  useTheme,
  VFlow,
} from 'bold-ui'
import { Authorization } from 'components/auth/model-authorization'
import { FooterButton } from 'components/footer-button'
import { Form, FormRenderProps } from 'components/form'
import DateRangeField from 'components/form/field/DateRangeField'
import { CiapCidSelectModel } from 'components/form/field/select/CiapCidSelectField/CiapCidSelectField'
import { RacaCorSelectField } from 'components/form/field/select/RacaCorSelectField'
import { TitleGroup } from 'components/TitleGroup'
import { FormApi } from 'final-form'
import { AcompanhamentoCondicaoSaudeTematicaEnum } from 'graphql/types.generated'
import { Fragment, useMemo } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'
import { AcompanhamentoCondicaoSaudeProblemasCondicoesFilter } from 'view/acompanhamento/acompanhamento-condicao-saude/components/AcompanhamentoCondicaoSaudeProblemasCondicoesFilter'
import { FaixaEtariaFilterGroup } from 'view/acompanhamento/components/FaixaEtariaFilterGroup'
import { SexoIdentidadeGeneroButtonGroup } from 'view/acompanhamento/components/SexoIdentidadeGeneroButtonGroup'

import { calculatorFiltrosAvancados } from '../calculator-acompanhamentoCondicaoSaude'
import {
  AcompanhamentoCondicaoSaudeFormModel,
  filterDefaultAcompanhamentoCondicaoSaude,
} from '../model/model-acompanhamentoCondicaoSaude'
import { validateFiltrosAvancados } from '../validator-acompanhamentoCondicaoSaude'
import { IvcfFilterSection } from './IvcfFilterSection'

export const FILTROS_AVANCADOS_URL = '/filtros-avancados'

interface AcompanhamentoCondicaoSaudeFiltrosAvancadosModalProps {
  permission: Authorization
  dateRange: DateRange
  now: Date
  initialValues: AcompanhamentoCondicaoSaudeFormModel
  onChangeCiapCid: (ciapCidList: CiapCidSelectModel[]) => void
  onSubmit(values: AcompanhamentoCondicaoSaudeFormModel): void
}

const meta = metaPath<AcompanhamentoCondicaoSaudeFormModel>()

export const AcompanhamentoCondicaoSaudeFiltrosAvancadosModal = (
  props: AcompanhamentoCondicaoSaudeFiltrosAvancadosModalProps
) => {
  const { permission, dateRange, now, initialValues, onChangeCiapCid, onSubmit } = props

  const history = useHistory()
  const match = useRouteMatch()

  const styles = createStyles(useTheme())

  const handleSubmit = (values: AcompanhamentoCondicaoSaudeFormModel) => {
    onSubmit(values)
    handleClose()
  }

  const handleResetFilterToTematicaDefault = (
    values: AcompanhamentoCondicaoSaudeFormModel,
    form: FormApi<AcompanhamentoCondicaoSaudeFormModel>
  ) => {
    const filtersDefault = filterDefaultAcompanhamentoCondicaoSaude[values.listaTematica]
    form.reset(filtersDefault)
  }

  const handleClose = () => {
    history.push(match.url.replace(FILTROS_AVANCADOS_URL, ''))
  }

  const decorators = useMemo(() => [calculatorFiltrosAvancados], [])

  const renderForm = ({ form, invalid, values }: FormRenderProps<AcompanhamentoCondicaoSaudeFormModel>) => {
    const canCheckApenasProblemasCondicoesAtivas =
      !values || (isEmpty(values.grupoCondicao) && isEmpty(values.problemasCondicoes))

    return (
      <Modal open size='large' onClose={handleClose} closeOnBackdropClick>
        <ModalBody>
          <VFlow>
            <TitleGroup title='Filtros avançados' />
            <Grid>
              <VFlow>
                <AcompanhamentoCondicaoSaudeProblemasCondicoesFilter
                  permission={permission}
                  path={meta}
                  canCheckApenasProblemasCondicoesAtivas={canCheckApenasProblemasCondicoesAtivas}
                  isInsideModal
                  handleChangeCiapCid={onChangeCiapCid}
                />
                <Cell size={9}>
                  <SexoIdentidadeGeneroButtonGroup onChange={form.change} meta={meta} />
                </Cell>
                <Cell size={12}>
                  <VFlow>
                    <Heading level={3}>Faixa etária</Heading>
                    <FaixaEtariaFilterGroup
                      change={form.change}
                      meta={meta}
                      faixaEtariaFilter={values.tipoFaixaEtaria}
                      tipoVulnerabilidade={values.tipoVulnerabilidadeSelected}
                      isInsideModal
                    />
                  </VFlow>
                </Cell>
              </VFlow>
              <Cell size={12}>
                <hr css={styles.divider} />
              </Cell>
              <Cell size={5}>
                <VFlow>
                  <Heading level={3}>Raça/cor</Heading>
                  <RacaCorSelectField name={meta.racaCor}></RacaCorSelectField>
                </VFlow>
              </Cell>
              <Cell size={12}>
                <hr css={styles.divider} />
              </Cell>
              <Cell size={6}>
                <VFlow>
                  <Heading level={3}>Período do último atendimento</Heading>
                  <DateRangeField
                    name={meta.periodoUltimoAtendimento.absolutePath()}
                    // TODO eldorado #916 (bold): corrigir as props startPlaceholder e finalPlaceholder
                    // startPlaceholder='Data inicial'
                    // finalPlaceholder='Data final'
                    initialValue={dateRange}
                    maxDate={now}
                  />
                </VFlow>
              </Cell>
              {initialValues.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.PESSOA_IDOSA && (
                <Fragment>
                  <Cell size={12}>
                    <hr css={styles.divider} />
                  </Cell>
                  <Cell size={12}>
                    <IvcfFilterSection meta={meta.ivcf} values={values.ivcf} onChange={form.change} />
                  </Cell>
                </Fragment>
              )}
            </Grid>
          </VFlow>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='space-between'>
            <FooterButton onClick={() => handleResetFilterToTematicaDefault(values, form)}>
              <Icon icon='redo' style={styles.icon} />
              Voltar para padrão
            </FooterButton>
            <HFlow>
              <FooterButton onClick={handleClose}>Cancelar</FooterButton>
              <Tooltip text={invalid && 'Existem campos preenchidos de forma incorreta.'}>
                <FooterButton kind='primary' disabled={invalid} onClick={() => handleSubmit(values)}>
                  Aplicar
                </FooterButton>
              </Tooltip>
            </HFlow>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Form<AcompanhamentoCondicaoSaudeFormModel>
      decorators={decorators}
      render={renderForm}
      initialValues={initialValues}
      validateOnBlur
      validate={validateFiltrosAvancados}
    />
  )
}

const createStyles = (theme: Theme) => ({
  divider: css`
    margin: 1rem 0;
    background: ${theme.pallete.divider};
  `,
  icon: css`
    margin-right: 0.5rem;
  `,
})
